import { INotification } from '../interfaces'
import { ClockCircleOutlined } from '@ant-design/icons'
import { Avatar, Badge, Dropdown, Empty, Tabs, notification } from 'antd'
import { useAuth } from 'auth'
import { SpinLoading } from 'components/atoms'
import { useProfile } from 'hooks'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { useNotificationService } from 'services'
import { ESocketEventName, socketIoService } from 'socket'
import { notificationSelector, setCountUnread } from 'store/notificationSlice'
import styled from 'styled-components'
import { formatDate } from 'utils'

export const StyledButtonIcon = styled.div`
  margin-left: 12px;
  width: 34px;
  height: 34px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-shadow: 0px 1px 30px rgba(85, 85, 85, 0.1);
  border-radius: 12px;
`

export const StyledWrapper = styled.div`
  padding: 16px 16px 0 16px;
  max-height: 500px;
  overflow: auto;
`
export const StyledUl = styled.ul`
  box-shadow: none;
  list-style: none;
  padding: 0;
  margin-bottom: 0;
  li {
    border: 1px solid var(--color-gray-103);
    border-radius: 4px;
    padding: 8px;
    cursor: pointer;
    .item-send-time {
      color: var(--color-primary-108);
      min-width: 120px;
      white-space: nowrap;
      font-size: 1.2rem;
      line-height: 20px;
    }
  }
  li.noti-unread {
    border-color: var(--color-primary-108);
  }
  li:last-child div::after {
    display: none !important;
  }
  li + li {
    margin-top: 4px;
  }
  li:hover {
    background-color: var(--color-gray-102);
  }
`

export const StyledNotiContent = styled.div`
  background: white;
  box-shadow: 0 3px 6px -4px rgb(0 0 0 / 12%), 0 6px 16px 0 rgb(0 0 0 / 8%),
    0 9px 28px 8px rgb(0 0 0 / 5%);
  border-radius: 6px;
  &::before {
    content: '';
    border-bottom: 10px solid white;
    border-left: 16px solid transparent;
    border-right: 16px solid transparent;
    position: absolute;
    top: -10px;
    left: 110px;
  }
  ul {
    box-shadow: none;
  }
  .noti-title {
    font-weight: 600;
    margin-bottom: 4px;
  }
  .item-title {
    font-weight: 600;
    display: flex;
    justify-content: space-between;
  }
  .ant-tabs-nav {
    margin: 0;
  }
  .ant-spin-nested-loading > div > .ant-spin {
    max-height: 100%;
  }
`

export const StyledDropdown = styled(Dropdown)`
  .ant-dropdown::before {
    display: none !important;
  }
  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none; /* Standard syntax */
`
export const StyledTitle = styled.div`
  padding: 12px 16px 0px 16px;
  font-weight: 600;
`
export const StyledViewMore = styled.div`
  padding: 12px;
  text-align: center;
  height: 48px;
  color: var(--color-primary-primary);
  span {
    cursor: pointer;
    font-weight: 600;
    height: 12px;
    &:hover {
      letter-spacing: 0.3px;
    }
  }
`

enum ETab {
  UnRead = '1',
  All = '0'
}

const BookingNotification = () => {
  const { getNotifications, getCountUnread, getNotificationsDetail } =
    useNotificationService()
  const { countUnread } = useSelector(notificationSelector)
  const { isSysAdmin } = useAuth()
  const { profile } = useProfile()
  const userRole = profile?.my_clinic_user?.clinic_role.permission
  const [notifications, setNotifications] = useState<Array<INotification>>([])
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [activeKey, setActiveKey] = useState<string>(ETab.UnRead)
  const LIMIT_PAGE = 20
  const page = useRef(0)
  const [stillNoti, setStillNoti] = useState(false)
  const [loading, setLoading] = useState(false)
  const notificationsRendered = useRef<{ [key: string]: boolean }>({})
  const countUnreadTempRef = useRef<number>(countUnread)
  const types: string[] = isSysAdmin ? [] : ['notifications']
  if (!isSysAdmin) {
    if (userRole?.examination?.examination) {
      types.push('examination')
    }
    if (userRole?.patient_management?.transaction) {
      types.push('withdrawal')
    }
    if (
      userRole?.account_management &&
      Object.keys(userRole?.account_management).length > 0
    ) {
      types.push('clinic_registration')
    }

    if (userRole?.patient_management?.booking) {
      types.push('booking')
      types.push('clinic_booking')
    }
  }
  useEffect(() => {
    handleGetCountUnread()
    const handleShowNotification = (data: any) => {
      if (data.Type === 'booking') {
        if (userRole?.patient_management?.booking) {
          if (data?.SubType === 'created') {
            notification.info({
              message: data?.Detail?.title || 'booking',
              description: data?.Detail?.message || '',
              top: 1,
              onClick: () => handleNotificationClick(data.Detail)
            })
            updateCountUnread()
          }
          if (data?.SubType === 'canceled') {
            notification.info({
              message: data?.Detail?.title || 'booking',
              description: data?.Detail?.message || '',
              top: 1,
              onClick: () => handleNotificationClick(data.Detail)
            })
            updateCountUnread()
          }
        } else return
      }
      if (data.SubType === '' && data.Type === 'notifications') {
        notification.info({
          message: data?.Detail?.title || 'notification',
          description: data?.Detail?.message || '',
          top: 1
        })
        updateCountUnread()
      }
      if (data.Type === 'withdrawal') {
        if (userRole?.patient_management?.transaction) {
          if (data.SubType === 'approved') {
            notification.info({
              message: data?.Detail?.title || 'withdrawal',
              description: data?.Detail?.message || '',
              top: 1
            })
            updateCountUnread()
          }
          if (data.SubType === 'rejected') {
            notification.info({
              message: data?.Detail?.title || 'withdrawal',
              description: data?.Detail?.message || '',
              top: 1
            })
            updateCountUnread()
          }
        } else return
      }
    }
    if (!socketIoService.getSocketConnection()) socketIoService.initSocket()
    socketIoService.subscribeNotification(handleShowNotification)
    socketIoService.subscribeAllClinicNotification(handleShowNotification)
    return () => {
      socketIoService.unsubscribeEvent(
        ESocketEventName.MANAGE_NOTIFICATION_TOPIC
      )
      socketIoService.unsubscribeEvent(
        ESocketEventName.MANAGE_NOTIFICATION_TOPIC_ALL_CLINIC
      )
    }
  }, [userRole])

  const updateCountUnread = () => {
    const countUnreadUpdated = countUnreadTempRef.current + 1
    dispatch(setCountUnread(countUnreadUpdated))
  }

  const handleNotificationClick = (noti?: any) => {
    navigate(`/booking/receive/${noti?.booking_id}`)
    notification.destroy()
  }

  useLayoutEffect(() => {
    if (open) {
      page.current = 0
      setLoading(true)
      handleGetNotifications()
    }
  }, [activeKey, open])

  useLayoutEffect(() => {
    if (open) {
      handleGetNotifications(undefined, true)
    }
  }, [countUnread])
  const handleGetCountUnread = () => {
    getCountUnread({
      query: isSysAdmin ? {} : { types: types }
    }).then((res) => {
      dispatch(setCountUnread(res.data?.unread_count))
      countUnreadTempRef.current = res.data?.unread_count
    })
  }

  const handleGetNotifications = async (
    os?: number,
    isGetNewNoti?: boolean
  ) => {
    getNotifications({
      offset: os || page.current * LIMIT_PAGE,
      limit: LIMIT_PAGE,
      query: isSysAdmin
        ? activeKey === ETab.UnRead
          ? {
              unread_only: true
            }
          : undefined
        : activeKey === ETab.UnRead
        ? { unread_only: true, types: types }
        : { types: types }
    })
      .then((res) => {
        const uniqueNotifications = res?.data?.notifications.filter(
          (newNoti) =>
            !notifications.some((existing) => existing.id === newNoti.id)
        )
        if (res?._successfully) {
          if (isGetNewNoti) {
            handleAddNewNoti(uniqueNotifications)
          } else {
            setNotifications((prev) => [...prev, ...uniqueNotifications])
          }
          setStillNoti(res?.data?.notifications?.length === 20)
        }
      })
      .finally(() => setLoading(false))
  }

  const handleAddNewNoti = (notis: Array<INotification>) => {
    const notifications: any = notificationsRendered.current
    const newNotis = notis?.filter(
      (item) => item?.id && !notifications[item?.id]
    )
    setNotifications([...newNotis, ...notifications])
  }

  const handleViewMore = () => {
    setLoading(true)
    page.current = page.current + 10
    handleGetNotifications(page.current)
  }

  const menu = () => {
    const handleClick = (noti?: INotification) => {
      if (noti?.type === 'withdrawal') navigate(`/booking/transaction`)
      if (noti?.detail?.booking_id)
        navigate(`/booking/receive/${noti?.detail?.booking_id}`)
      getNotificationsDetail(noti?.id).finally(() => handleGetCountUnread())
      setOpen(false)
    }
    const itemsMenu = notifications?.map(
      (noti: INotification, index: number) => {
        if (true) {
          const notis: any = notificationsRendered.current
          if (noti?.id) {
            notis[noti.id] = true
          }
        }
        return (
          <li
            key={`${noti.id}-${index}`}
            className={noti?.read ? '' : 'noti-unread'}
            onClick={() => handleClick(noti)}>
            <div className="item-title">
              <span>{noti?.detail?.title}</span>
              {noti?.detail?.sent_at && (
                <span className="item-send-time">
                  <ClockCircleOutlined />{' '}
                  {formatDate(noti?.detail?.sent_at, 'DD/MM/YYYY HH:mm')}
                </span>
              )}
            </div>
            <div className="item-message">{noti?.detail?.message}</div>
          </li>
        )
      }
    )
    return (
      <StyledNotiContent>
        <SpinLoading loading={loading}>
          <StyledTitle className="noti-title">{t('notification')}</StyledTitle>
          <Tabs
            centered
            activeKey={activeKey}
            onChange={setActiveKey}
            items={[
              {
                label: t('unread'),
                key: ETab.UnRead,
                children: (
                  <StyledWrapper>
                    {notifications?.length > 0 ? (
                      <StyledUl>{itemsMenu}</StyledUl>
                    ) : (
                      <Empty description={t('no-new-notification')} />
                    )}
                  </StyledWrapper>
                )
              },
              {
                label: t('all'),
                key: ETab.All,
                children: (
                  <StyledWrapper>
                    {notifications?.length > 0 ? (
                      <StyledUl>{itemsMenu}</StyledUl>
                    ) : (
                      <Empty description={t('no-new-notification')} />
                    )}
                  </StyledWrapper>
                )
              }
            ]}
          />
          <StyledViewMore>
            {stillNoti && (
              <span onClick={handleViewMore}>{t('view-more')}</span>
            )}
          </StyledViewMore>
        </SpinLoading>
      </StyledNotiContent>
    )
  }

  return (
    <>
      <Badge count={countUnread} overflowCount={9999}>
        <StyledDropdown
          overlay={menu}
          trigger={['click']}
          overlayClassName="booking-notification"
          open={open}
          onOpenChange={setOpen}>
          <StyledButtonIcon className="button-icon">
            <Avatar size={24} icon={<img src="/images/ic-bell.svg" alt="" />} />
          </StyledButtonIcon>
        </StyledDropdown>
      </Badge>
    </>
  )
}

export default BookingNotification
